import type { Loan } from 'app/types/loan';

type LoanProposal = Pick<Loan, 'id' | 'isUpdateFor' | 'createdAt'>;

/** Keep only the newest proposal in each chain of date changes actionable. */
export function getSupersededLoanIds(loans: readonly LoanProposal[]): Set<string> {
  const loansById = new Map(loans.map((loan) => [loan.id, loan]));
  const latestByRoot = new Map<string, { loan: LoanProposal; depth: number }>();
  const supersededIds = new Set<string>();

  for (const loan of loansById.values()) {
    let rootId = loan.id;
    let parentId = loan.isUpdateFor;
    const ancestors = new Set<string>([loan.id]);

    while (parentId && !ancestors.has(parentId)) {
      ancestors.add(parentId);
      supersededIds.add(parentId);
      rootId = parentId;
      parentId = loansById.get(parentId)?.isUpdateFor;
    }

    const depth = ancestors.size;
    const latest = latestByRoot.get(rootId);
    if (!latest) {
      latestByRoot.set(rootId, { loan, depth });
      continue;
    }

    const dateDifference = new Date(loan.createdAt).getTime() - new Date(latest.loan.createdAt).getTime();
    if (
      dateDifference > 0 ||
      (dateDifference === 0 && (depth > latest.depth || (depth === latest.depth && loan.id > latest.loan.id)))
    ) {
      supersededIds.add(latest.loan.id);
      latestByRoot.set(rootId, { loan, depth });
    } else {
      supersededIds.add(loan.id);
    }
  }

  return supersededIds;
}
